import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import EmotionalTimeline from './components/EmotionalTimeline';
import MoodDistribution from './components/MoodDistribution';
import ThemeAnalysis from './components/ThemeAnalysis';
import ResilienceMetrics from './components/ResilienceMetrics';
import TriggerInsights from './components/TriggerInsights';
import MilestoneMoments from './components/MilestoneMoments';
import InsightsSummary from './components/InsightsSummary';
import './InsightsPage.css';

function InsightsPage() {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('quarter');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  // Scroll to top when tab changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  const loadInsights = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const data = await api.insights.getDashboard({ period, timezone });
      
      // Check for insufficient data response
      if (data && data.message && !data.emotionalTimeline) {
        setInsights({ insufficientData: true, message: data.message });
        return;
      }
      
      // Validate essential data structure
      if (!data || !data.aiSummary || !data.emotionalTimeline) {
        throw new Error('Invalid data structure received from server');
      }
      
      setInsights(data);
    } catch (err) {
      setError(err.message || 'Failed to load insights');
      console.error('Failed to load insights:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="insights-page">
        <div className="insights-page__container">
          <div className="insights-page__loading">
            <div className="insights-page__spinner"></div>
            <p>Analyzing your emotional journey...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="insights-page">
        <div className="insights-page__container">
          <div className="insights-page__error">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <h3>Unable to load insights</h3>
            <p>{error}</p>
            <button onClick={loadInsights} className="insights-page__retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!insights) return null;

  // Handle insufficient data case – encouraging quote, no stats/features
  if (insights.insufficientData) {
    return (
      <div className="insights-page">
        <div className="insights-page__container insights-page__container--empty">
          <button 
            className="insights-page__empty-close"
            onClick={() => navigate('/')}
            aria-label="Back to home"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          
          <div className="insights-page__encourage">
            <blockquote className="insights-page__quote">
              &ldquo;The best insights come to those who keep showing up. Add a little each day—soon you&rsquo;ll see what your journal has been learning about you.&rdquo;
            </blockquote>
            <p className="insights-page__encourage-sub">
              Keep adding entries every day. Your AI-powered insights are just around the corner.
            </p>
            <button 
              className="insights-page__encourage-cta"
              onClick={() => navigate('/')}
            >
              Write today&rsquo;s entry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="insights-page">
      <div className="insights-page__container">
        {/* Header */}
        <header className="insights-page__header">
          <div className="insights-page__header-top">
            <button 
              className="insights-page__close"
              onClick={() => navigate('/')}
              aria-label="Back to home"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
            </button>
            <h1 className="insights-page__title">Your Insights</h1>
            <div className="insights-page__period-selector">
              <select 
                value={period} 
                onChange={(e) => setPeriod(e.target.value)}
                className="insights-page__period-select"
              >
                <option value="month">Past Month</option>
                <option value="quarter">Past 3 Months</option>
                <option value="year">Past Year</option>
                <option value="all">All Time</option>
              </select>
            </div>
          </div>

          {/* AI Summary Headline */}
          {insights.aiSummary && (
            <div className="insights-page__headline">
              <div className="insights-page__headline-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <div>
                <h2>{insights.aiSummary.headline}</h2>
                <p>{insights.aiSummary.overview}</p>
              </div>
            </div>
          )}
        </header>

        {/* Navigation Tabs */}
        <nav className="insights-page__nav">
          <button
            className={`insights-page__nav-btn ${activeTab === 'overview' ? 'insights-page__nav-btn--active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
            </svg>
            Overview
          </button>
          <button
            className={`insights-page__nav-btn ${activeTab === 'patterns' ? 'insights-page__nav-btn--active' : ''}`}
            onClick={() => setActiveTab('patterns')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v18h18"/>
              <path d="M18 17V9M13 17v-6M8 17v-3"/>
            </svg>
            Patterns
          </button>
          <button
            className={`insights-page__nav-btn ${activeTab === 'themes' ? 'insights-page__nav-btn--active' : ''}`}
            onClick={() => setActiveTab('themes')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5"/>
            </svg>
            Themes
          </button>
          <button
            className={`insights-page__nav-btn ${activeTab === 'growth' ? 'insights-page__nav-btn--active' : ''}`}
            onClick={() => setActiveTab('growth')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
            </svg>
            Growth
          </button>
        </nav>

        {/* Content Area */}
        <div className="insights-page__content" ref={contentRef}>
          {activeTab === 'overview' && (
            <div className="insights-page__tab-content">
              {/* Highlights Cards */}
              {insights.aiSummary?.highlights && insights.aiSummary.highlights.length > 0 && (
                <div className="insights-page__highlights">
                  {insights.aiSummary.highlights.map((highlight, index) => (
                    <div key={index} className="insights-page__highlight-card">
                      <span className="insights-page__highlight-icon">{highlight.icon}</span>
                      <div className="insights-page__highlight-content">
                        <div className="insights-page__highlight-achievement">{highlight.achievement}</div>
                        <div className="insights-page__highlight-description">{highlight.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Main Emotional Timeline Graph */}
              {insights.emotionalTimeline && (
                <EmotionalTimeline data={insights.emotionalTimeline} />
              )}

              {/* Quick Stats Grid */}
              {insights.writingPatterns && (
                <div className="insights-page__stats-grid">
                  <div className="insights-page__stat-card">
                    <div className="insights-page__stat-icon insights-page__stat-icon--entries">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                      </svg>
                    </div>
                    <div className="insights-page__stat-value">
                      {insights.writingPatterns.totalEntries || 0}
                    </div>
                    <div className="insights-page__stat-label">Total Entries</div>
                  </div>

                  <div className="insights-page__stat-card">
                    <div className="insights-page__stat-icon insights-page__stat-icon--streak">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                      </svg>
                    </div>
                    <div className="insights-page__stat-value">
                      {insights.writingPatterns.streak?.current || 0}
                    </div>
                    <div className="insights-page__stat-label">Day Streak</div>
                  </div>

                  {insights.resilienceMetrics && (
                    <div className="insights-page__stat-card">
                      <div className="insights-page__stat-icon insights-page__stat-icon--resilience">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                        </svg>
                      </div>
                      <div className="insights-page__stat-value">
                        {insights.resilienceMetrics.resilienceScore || 0}/10
                      </div>
                      <div className="insights-page__stat-label">Resilience Score</div>
                    </div>
                  )}

                  {insights.emotionalVelocity && (
                    <div className="insights-page__stat-card">
                      <div className="insights-page__stat-icon insights-page__stat-icon--stability">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                        </svg>
                      </div>
                      <div className="insights-page__stat-value">
                        {insights.emotionalVelocity.stabilityScore || 0}/10
                      </div>
                      <div className="insights-page__stat-label">Emotional Stability</div>
                    </div>
                  )}
                </div>
              )}

              {/* Insights Summary */}
              {insights.aiSummary && (
                <InsightsSummary summary={insights.aiSummary} />
              )}
            </div>
          )}

          {activeTab === 'patterns' && (
            <div className="insights-page__tab-content">
              {insights.moodDistribution && (
                <MoodDistribution data={insights.moodDistribution} />
              )}
              {insights.triggers && (
                <TriggerInsights triggers={insights.triggers} />
              )}
            </div>
          )}

          {activeTab === 'themes' && (
            <div className="insights-page__tab-content">
              {insights.themeAnalysis && (
                <ThemeAnalysis themes={insights.themeAnalysis} />
              )}
              {insights.milestones && (
                <MilestoneMoments milestones={insights.milestones} />
              )}
            </div>
          )}

          {activeTab === 'growth' && (
            <div className="insights-page__tab-content">
              {/* Areas of Growth - Primary focus for Growth tab */}
              {insights.aiSummary?.areasOfGrowth && insights.aiSummary.areasOfGrowth.length > 0 && (
                <div style={{ marginBottom: '32px' }}>
                  <InsightsSummary summary={{ areasOfGrowth: insights.aiSummary.areasOfGrowth }} />
                </div>
              )}
              
              {/* Resilience Metrics */}
              {insights.resilienceMetrics || insights.emotionalVelocity || insights.comparativeInsights ? (
                <ResilienceMetrics 
                  resilience={insights.resilienceMetrics}
                  velocity={insights.emotionalVelocity}
                  comparative={insights.comparativeInsights}
                />
              ) : insights.aiSummary?.areasOfGrowth && insights.aiSummary.areasOfGrowth.length > 0 ? null : (
                <div className="insights-page__no-data">
                  <p>Growth metrics will be available once you have more entries.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InsightsPage;
